import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Pencil, Plus, Trash2, Building2, MapPin, Layers, Home as HomeIcon, X, Save, ChevronDown, ChevronRight } from 'lucide-react';
import { api, friendlyError } from '../lib/api';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const PROPERTY_TYPES = ['apartamento', 'apartaestudio', 'casa', 'oficina', 'local', 'lote', 'bodega'];
const UNIT_STATUSES = ['disponible', 'reservado', 'vendido', 'bloqueado'];
const REVIEW_LABEL = { borrador: 'Borrador', en_revision: 'En revisión', aprobado: 'Aprobado', publicado: 'Publicado' };
const STATUS_STYLE = {
  disponible: 'bg-emerald-100 text-emerald-800',
  reservado: 'bg-amber-100 text-amber-800',
  vendido: 'bg-gray-200 text-gray-600',
  bloqueado: 'bg-rose-100 text-rose-700',
};
const fmtCOP = (n) => (n == null || n === '' ? '—' : `$${Number(n).toLocaleString('es-CO')}`);

function Field({ label, children, span }) {
  return (
    <label className={`text-sm ${span ? 'sm:col-span-2' : ''}`}>
      <span className="text-[11px] uppercase tracking-widest text-gray-500">{label}</span>
      {children}
    </label>
  );
}
const inputCls = 'mt-1 w-full h-10 px-3 rounded-xl border border-gray-200 focus:border-habita outline-none text-sm';

const EMPTY_TYP = {
  name: '', property_type: 'apartamento', beds: '', baths: '', parking: '0', storage: '0',
  area_min: '', area_max: '', price_from: '', amenities: '', image_url: '', description: '',
};

function TypologyForm({ initial, onSave, onCancel, saving }) {
  const [f, setF] = useState(initial || EMPTY_TYP);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = (e) => {
    e.preventDefault();
    const body = {
      name: f.name.trim(),
      property_type: f.property_type,
      beds: Number(f.beds), baths: Number(f.baths),
      parking: Number(f.parking || 0), storage: Number(f.storage || 0),
      area_min: Number(f.area_min),
      area_max: f.area_max === '' ? null : Number(f.area_max),
      price_from: Number(f.price_from),
      amenities: f.amenities ? f.amenities.split(',').map((s) => s.trim()).filter(Boolean) : [],
      images: f.image_url ? [f.image_url.trim()] : [],
      description: f.description || null,
    };
    onSave(body);
  };
  return (
    <form onSubmit={submit} className="rounded-2xl border border-gray-200 bg-gray-50/60 p-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="typology-form">
      <Field label="Nombre de la tipología *" span>
        <input required value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="Apto 2 habitaciones" className={inputCls} data-testid="typ-field-name" />
      </Field>
      <Field label="Tipo de inmueble">
        <select value={f.property_type} onChange={(e) => set('property_type', e.target.value)} className={inputCls} data-testid="typ-field-type">
          {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="Precio desde (COP) *">
        <input required type="number" min="0" value={f.price_from} onChange={(e) => set('price_from', e.target.value)} className={inputCls} data-testid="typ-field-price" />
      </Field>
      <Field label="Habitaciones *">
        <input required type="number" min="0" value={f.beds} onChange={(e) => set('beds', e.target.value)} className={inputCls} data-testid="typ-field-beds" />
      </Field>
      <Field label="Baños *">
        <input required type="number" min="0" step="0.5" value={f.baths} onChange={(e) => set('baths', e.target.value)} className={inputCls} data-testid="typ-field-baths" />
      </Field>
      <Field label="Parqueaderos">
        <input type="number" min="0" value={f.parking} onChange={(e) => set('parking', e.target.value)} className={inputCls} data-testid="typ-field-parking" />
      </Field>
      <Field label="Depósitos">
        <input type="number" min="0" value={f.storage} onChange={(e) => set('storage', e.target.value)} className={inputCls} data-testid="typ-field-storage" />
      </Field>
      <Field label="Área mín (m²) *">
        <input required type="number" min="0" value={f.area_min} onChange={(e) => set('area_min', e.target.value)} className={inputCls} data-testid="typ-field-area-min" />
      </Field>
      <Field label="Área máx (m²)">
        <input type="number" min="0" value={f.area_max} onChange={(e) => set('area_max', e.target.value)} className={inputCls} data-testid="typ-field-area-max" />
      </Field>
      <Field label="Amenidades (separadas por coma)" span>
        <input value={f.amenities} onChange={(e) => set('amenities', e.target.value)} placeholder="piscina, gimnasio, ascensor" className={inputCls} data-testid="typ-field-amenities" />
      </Field>
      <Field label="URL imagen / plano" span>
        <input value={f.image_url} onChange={(e) => set('image_url', e.target.value)} className={inputCls} data-testid="typ-field-image" />
      </Field>
      <div className="sm:col-span-2 flex gap-2">
        <Button type="submit" disabled={saving} className="rounded-full bg-habita hover:bg-habita-dark text-white" data-testid="typ-save-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Guardando…' : 'Guardar tipología'}
        </Button>
        <Button type="button" variant="outline" className="rounded-full" onClick={onCancel}><X className="w-4 h-4 mr-2" /> Cancelar</Button>
      </div>
    </form>
  );
}

const EMPTY_UNIT = { tower: '', unit_number: '', floor: '', area_m2: '', price: '', parking: '', storage: '', status: 'disponible' };

function UnitForm({ initial, onSave, onCancel, saving }) {
  const [f, setF] = useState(initial || EMPTY_UNIT);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = (e) => {
    e.preventDefault();
    onSave({
      tower: f.tower || null,
      unit_number: f.unit_number.trim(),
      floor: f.floor === '' ? null : parseInt(f.floor, 10),
      area_m2: f.area_m2 === '' ? null : Number(f.area_m2),
      price: f.price === '' ? null : Number(f.price),
      parking: f.parking === '' ? null : Number(f.parking),
      storage: f.storage === '' ? null : Number(f.storage),
      status: f.status,
    });
  };
  return (
    <form onSubmit={submit} className="rounded-xl border border-gray-200 bg-white p-3 grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2" data-testid="unit-form">
      <Field label="Torre"><input value={f.tower} onChange={(e) => set('tower', e.target.value)} className={inputCls} data-testid="unit-field-tower" /></Field>
      <Field label="N° unidad *"><input required value={f.unit_number} onChange={(e) => set('unit_number', e.target.value)} className={inputCls} data-testid="unit-field-number" /></Field>
      <Field label="Piso"><input type="number" value={f.floor} onChange={(e) => set('floor', e.target.value)} className={inputCls} data-testid="unit-field-floor" /></Field>
      <Field label="Área (m²)"><input type="number" value={f.area_m2} onChange={(e) => set('area_m2', e.target.value)} className={inputCls} data-testid="unit-field-area" /></Field>
      <Field label="Precio (COP)"><input type="number" value={f.price} onChange={(e) => set('price', e.target.value)} className={inputCls} data-testid="unit-field-price" /></Field>
      <Field label="Parqueaderos"><input type="number" value={f.parking} onChange={(e) => set('parking', e.target.value)} className={inputCls} data-testid="unit-field-parking" /></Field>
      <Field label="Depósitos"><input type="number" value={f.storage} onChange={(e) => set('storage', e.target.value)} className={inputCls} data-testid="unit-field-storage" /></Field>
      <Field label="Estado">
        <select value={f.status} onChange={(e) => set('status', e.target.value)} className={inputCls} data-testid="unit-field-status">
          {UNIT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </Field>
      <div className="col-span-2 sm:col-span-4 flex gap-2">
        <Button type="submit" disabled={saving} className="rounded-full h-9 bg-habita hover:bg-habita-dark text-white" data-testid="unit-save-btn">
          <Save className="w-4 h-4 mr-1" /> {saving ? 'Guardando…' : 'Guardar unidad'}
        </Button>
        <Button type="button" variant="outline" className="rounded-full h-9" onClick={onCancel}>Cancelar</Button>
      </div>
    </form>
  );
}

function TypologyCard({ pid, typ, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [units, setUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [addingUnit, setAddingUnit] = useState(false);
  const [editUnit, setEditUnit] = useState(null);
  const [err, setErr] = useState('');

  const loadUnits = useCallback(async () => {
    setLoadingUnits(true);
    try { const r = await api.developerUnits(pid, { typology_id: typ.id }); setUnits(r.items || []); }
    catch (e) { setErr(friendlyError(e)); } finally { setLoadingUnits(false); }
  }, [pid, typ.id]);

  const toggle = () => { const n = !expanded; setExpanded(n); if (n && units.length === 0) loadUnits(); };

  const saveTyp = async (body) => {
    setSaving(true); setErr('');
    try { await api.updateDeveloperTypology(pid, typ.id, body); setEditing(false); onChanged(); }
    catch (e) { setErr(friendlyError(e)); } finally { setSaving(false); }
  };
  const removeTyp = async () => {
    if (!window.confirm(`¿Eliminar la tipología "${typ.name}" y todas sus unidades?`)) return;
    try { await api.deleteDeveloperTypology(pid, typ.id); onChanged(); }
    catch (e) { setErr(friendlyError(e)); }
  };
  const saveUnit = async (body) => {
    setSaving(true); setErr('');
    try {
      if (editUnit) await api.updateDeveloperUnit(pid, editUnit.id, body);
      else await api.createDeveloperUnit(pid, { ...body, typology_id: typ.id });
      setAddingUnit(false); setEditUnit(null); await loadUnits(); onChanged();
    } catch (e) { setErr(friendlyError(e)); } finally { setSaving(false); }
  };
  const removeUnit = async (u) => {
    if (!window.confirm(`¿Eliminar la unidad ${u.unit_number}?`)) return;
    try { await api.deleteDeveloperUnit(pid, u.id); await loadUnits(); onChanged(); }
    catch (e) { setErr(friendlyError(e)); }
  };

  if (editing) {
    const initial = {
      name: typ.name || '', property_type: typ.property_type || 'apartamento',
      beds: String(typ.beds ?? ''), baths: String(typ.baths ?? ''),
      parking: String(typ.parking ?? 0), storage: String(typ.storage ?? 0),
      area_min: String(typ.area_min ?? ''), area_max: typ.area_max == null ? '' : String(typ.area_max),
      price_from: String(typ.price_from ?? ''),
      amenities: (typ.amenities || []).join(', '), image_url: (typ.images || [])[0] || '',
      description: typ.description || '',
    };
    return (
      <div className="mb-3" data-testid={`typology-edit-${typ.id}`}>
        <TypologyForm initial={initial} onSave={saveTyp} onCancel={() => setEditing(false)} saving={saving} />
        {err && <p className="text-sm text-rose-600 mt-1">{err}</p>}
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden mb-3" data-testid={`typology-card-${typ.id}`}>
      <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="w-16 h-16 rounded-xl bg-habita-mint flex items-center justify-center shrink-0 overflow-hidden">
          {(typ.images || [])[0] ? <img src={typ.images[0]} alt={typ.name} className="w-full h-full object-cover" /> : <Layers className="w-7 h-7 text-habita" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-habita-dark" data-testid={`typology-name-${typ.id}`}>{typ.name}</h3>
            <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-gray-100 text-gray-600">{typ.property_type}</span>
          </div>
          <div className="flex items-center gap-3 mt-1 text-sm text-gray-600 flex-wrap">
            <span>{typ.beds} hab · {typ.baths} baños</span>
            <span>{typ.area_min}{typ.area_max ? `–${typ.area_max}` : ''} m²</span>
            <span className="font-semibold text-habita-dark">Desde {fmtCOP(typ.price_from)}</span>
            {typ.parking > 0 && <span>{typ.parking} parq.</span>}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-700" data-testid={`typology-availability-${typ.id}`}>
              {typ.units_available ?? 0} disponibles / {typ.units_total ?? 0} unid.
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => setEditing(true)} data-testid={`typology-edit-btn-${typ.id}`}><Pencil className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" className="rounded-full text-rose-600 border-rose-200 hover:bg-rose-50" onClick={removeTyp} data-testid={`typology-delete-btn-${typ.id}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      </div>

      <button onClick={toggle} className="w-full flex items-center justify-between px-4 py-2 border-t border-gray-100 text-sm text-gray-600 hover:bg-gray-50" data-testid={`typology-units-toggle-${typ.id}`}>
        <span className="flex items-center gap-2"><HomeIcon className="w-4 h-4" /> Unidades ({typ.units_total ?? 0})</span>
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100" data-testid={`typology-units-panel-${typ.id}`}>
          {loadingUnits ? (
            <p className="text-sm text-gray-400 py-3">Cargando unidades…</p>
          ) : units.length === 0 ? (
            <p className="text-sm text-gray-400 py-3">Aún no hay unidades en esta tipología.</p>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-widest text-gray-400">
                    <th className="py-1 pr-3">Torre</th><th className="py-1 pr-3">Unidad</th><th className="py-1 pr-3">Piso</th>
                    <th className="py-1 pr-3">Área</th><th className="py-1 pr-3">Precio</th><th className="py-1 pr-3">Estado</th><th className="py-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {units.map((u) => editUnit && editUnit.id === u.id ? (
                    <tr key={u.id}><td colSpan={7}>
                      <UnitForm initial={{ tower: u.tower || '', unit_number: u.unit_number, floor: u.floor ?? '', area_m2: u.area_m2 ?? '', price: u.price ?? '', parking: u.parking ?? '', storage: u.storage ?? '', status: u.status }} onSave={saveUnit} onCancel={() => setEditUnit(null)} saving={saving} />
                    </td></tr>
                  ) : (
                    <tr key={u.id} className="border-t border-gray-50" data-testid={`unit-row-${u.id}`}>
                      <td className="py-2 pr-3">{u.tower || '—'}</td>
                      <td className="py-2 pr-3 font-medium">{u.unit_number}</td>
                      <td className="py-2 pr-3">{u.floor ?? '—'}</td>
                      <td className="py-2 pr-3">{u.area_m2 ? `${u.area_m2} m²` : '—'}</td>
                      <td className="py-2 pr-3">{fmtCOP(u.price)}</td>
                      <td className="py-2 pr-3"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${STATUS_STYLE[u.status] || ''}`}>{u.status}</span></td>
                      <td className="py-2 flex gap-1">
                        <button onClick={() => { setEditUnit(u); setAddingUnit(false); }} className="text-gray-400 hover:text-habita-dark" data-testid={`unit-edit-${u.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => removeUnit(u)} className="text-gray-400 hover:text-rose-600" data-testid={`unit-delete-${u.id}`}><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {addingUnit ? (
            <UnitForm onSave={saveUnit} onCancel={() => setAddingUnit(false)} saving={saving} />
          ) : (
            <Button variant="outline" size="sm" className="rounded-full mt-3" onClick={() => { setAddingUnit(true); setEditUnit(null); }} data-testid={`add-unit-btn-${typ.id}`}>
              <Plus className="w-4 h-4 mr-1" /> Agregar unidad
            </Button>
          )}
          {err && <p className="text-sm text-rose-600 mt-2">{err}</p>}
        </div>
      )}
    </div>
  );
}

export default function DeveloperProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [typs, setTyps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [savingTyp, setSavingTyp] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [p, t] = await Promise.all([api.developerProject(id), api.developerTypologies(id)]);
      setProject(p); setTyps(t.items || []);
    } catch (e) { setError(friendlyError(e)); } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const createTyp = async (body) => {
    setSavingTyp(true);
    try { await api.createDeveloperTypology(id, body); setAdding(false); await load(); }
    catch (e) { setError(friendlyError(e)); } finally { setSavingTyp(false); }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-10 h-10 border-4 border-habita border-t-transparent rounded-full animate-spin" /></div>;
  if (error) return <div className="max-w-3xl mx-auto px-4 py-16 text-center"><p className="text-rose-600" data-testid="project-detail-error">{error}</p></div>;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="developer-project-detail">
      <SEO title={`${project?.name} · Habita`} description="Gestiona tipologías y unidades del proyecto." />
      <Link to="/mi-constructora" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-habita-dark">
        <ArrowLeft className="w-4 h-4" /> Volver a mi constructora
      </Link>

      {/* Encabezado proyecto */}
      <div className="mt-3 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-14 h-14 rounded-xl bg-habita-mint flex items-center justify-center shrink-0 overflow-hidden">
            {project?.cover_url ? <img src={project.cover_url} alt={project.name} className="w-full h-full object-cover" /> : <Building2 className="w-7 h-7 text-habita" />}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-serif text-2xl font-black text-habita-dark" data-testid="project-detail-name">{project?.name}</h1>
              <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-gray-100 text-gray-700">{REVIEW_LABEL[project?.review_status] || 'Borrador'}</span>
            </div>
            {project?.city && <p className="text-sm text-gray-500 mt-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {project.city}{project.zone ? ` · ${project.zone}` : ''}</p>}
            <div className="flex items-center gap-3 mt-1 text-sm text-gray-600">
              <span>{project?.typologies_count ?? 0} tipologías</span>
              <span>{project?.units_available ?? 0} disp. / {project?.units_total ?? 0} unid.</span>
            </div>
          </div>
        </div>
        <Link to={`/mi-constructora/proyecto/${id}/editar`} data-testid="project-edit-btn">
          <Button variant="outline" className="rounded-full"><Pencil className="w-4 h-4 mr-2" /> Editar proyecto</Button>
        </Link>
      </div>

      {/* Tipologías */}
      <div className="flex items-center justify-between mt-8 mb-3">
        <h2 className="font-serif text-xl font-bold text-habita-dark">Tipologías</h2>
        {!adding && (
          <Button onClick={() => setAdding(true)} className="rounded-full bg-habita hover:bg-habita-dark text-white font-bold" data-testid="add-typology-btn">
            <Plus className="w-4 h-4 mr-2" /> Agregar tipología
          </Button>
        )}
      </div>

      {adding && <div className="mb-4"><TypologyForm onSave={createTyp} onCancel={() => setAdding(false)} saving={savingTyp} /></div>}

      {typs.length === 0 && !adding ? (
        <div className="rounded-2xl border-2 border-dashed border-gray-200 p-10 text-center" data-testid="typologies-empty">
          <Layers className="w-10 h-10 text-gray-300 mx-auto" />
          <p className="text-gray-600 mt-3 font-medium">Aún no tienes tipologías.</p>
          <p className="text-gray-400 text-sm">Crea la primera (p. ej. &quot;Apto 2 habitaciones&quot;) y luego agrega sus unidades.</p>
        </div>
      ) : (
        <div data-testid="typologies-list">
          {typs.map((t) => <TypologyCard key={t.id} pid={id} typ={t} onChanged={load} />)}
        </div>
      )}
    </div>
  );
}
